import * as React from 'react'

import { useAuthentication } from '@procurify/authentication'
import { logoutUrl } from '@procurify/authentication/src/configs'
import { SheetProvider, ModalProvider, ModalRoot } from '@procurify/ui'
import { theme } from '@procurify/ui-theme'
import { useSelector } from 'react-redux'
import { Redirect, Route, Switch, useHistory } from 'react-router'

import { ProtectedRoute } from '@webapp/app/components'
import { ThemeWrapper } from '@webapp/app/providers/ThemeWrapper'
import { UIProvider } from '@webapp/app/providers/UIProvider'
import { SettingsRouter } from '@webapp/app/routers/SettingsRouter'
import { ConnectedAppHeader } from '@webapp/components/AppHeader/ConnectedAppHeader'
import { ErrorBoundary } from '@webapp/components/ErrorBoundary'
import { GlobalAnalytics } from '@webapp/components/GlobalAnalytics'
import { GlobalErrorScreen } from '@webapp/components/GlobalErrorScreen'
import { Intercom } from '@webapp/components/Intercom'
import { getAppStatusMeta } from '@webapp/components/Navigation/utils'
import { SystemPushNotifications } from '@webapp/components/SystemPushNotifications'
import { PaginationProvider } from '@webapp/core/providers/Pagination/PaginationProvider'
import {
  makeSelectApplicationStatus,
  makeSelectIsAppReady,
  makeSelectRole,
  makeSelectSegmentAnalyticsMetadata,
  makeSelectUser,
} from '@webapp/core/stores/global'
import { ProcurifyDevToolsFfWarning } from '@webapp/dev/ProcurifyDevTools/ProcurifyDevToolsFfWarning'
import { ProcurifyDevToolsLoadable } from '@webapp/dev/ProcurifyDevTools/ProcurifyDevToolsLoadable'
import {
  useAppRoutes,
  useCheckNoPermission,
  useFeatureFlag,
} from '@webapp/hooks'
import { IS_DEV, IS_PROD } from '@webapp/infrastructure/envs'
import { AuthenticationErrorTypes } from '@webapp/models/enums/AuthenticationErrorType'
import { UnbilledItemPageRouter } from '@webapp/modules/accounts-payable/pages/Router'
import { ExpenseRouter } from '@webapp/modules/expense'
import { OrderRouter } from '@webapp/modules/purchasing'
import { NoPermissionsPage } from '@webapp/pages'
import { PaymentFinancialAccountPage } from '@webapp/pages/PaymentFinancialAccountPage'
import { RedirectPage } from '@webapp/pages/RedirectPage'
import { Permissions } from '@webapp/platform/iam'
import { AppLoader } from './AppLoader'
import { AppContentWrapper } from './components/AppContentWrapper/AppContentWrapper'
import { GlobalStyles } from './components/GlobalStyles'
import { ToastContainerWrapper } from './components/ToastContainerWrapper/ToastContainerWrapper'
import { useModuleConfig } from './config/useModuleConfig'
import { usePersistReferrerUrl } from './hooks/usePersistReferrerUrl'
import { BillRouter } from './routers/BillRouter/BillRouter'
import { ContractRouter } from './routers/ContractRouter'
import { DataAndInsightsRouter } from './routers/DataAndInsightsRouter'
import { InstanceManagementRouter } from './routers/InstanceManagementRouter'
import { IntegrationsRouter } from './routers/IntegrationsRouter'
import { PaymentRouter } from './routers/PaymentRouter'
import { ProcurementRouter } from './routers/ProcurementRouter'
import { PurchaseOrderRouter } from './routers/PurchaseOrderRouter'
import { ReceiveRouter } from './routers/ReceiveRouter'
import { SpendingCardRouter } from './routers/SpendingCardRouter'
import { UserProfileRouter } from './routers/UserProfileRouter'
import { VendorRouter } from './routers/VendorRouter'
import { AppStyled } from './styles/AppStyled'
import { AppContent } from '../components/AppContent'
import { ConnectedNavigation } from '../components/Navigation'
import { CatalogPage, DevelopmentPage, LoginPage, WelcomePage } from '../pages'

const AuthenticatedApp = () => {
  usePersistReferrerUrl()

  const { brp } = useAppRoutes()
  const user = useSelector(makeSelectUser())
  const segmentAnalyticsMetadata = useSelector(
    makeSelectSegmentAnalyticsMetadata()
  )

  const {
    routes: {
      enablePurchasingRoutes = false,
      enableEverythingButExpenseApRoutes = false,
    },
  } = useModuleConfig()

  // Check for trial mode and expired trial
  const applicationStatus = useSelector(makeSelectApplicationStatus())
  const role = useSelector(makeSelectRole())

  const CL_1324_NEW_NO_PERMISSION_PAGE = useFeatureFlag(
    'CL_1324_NEW_NO_PERMISSION_PAGE'
  )
  const AP_10029_UNBILLED_ITEMS_PAGE_MIGRATION = useFeatureFlag(
    'AP_10029_UNBILLED_ITEMS_PAGE_MIGRATION'
  )

  const hasNoPermission = useCheckNoPermission()

  const { isAdmin, isTrialMode, isTrialExpired } = getAppStatusMeta(
    applicationStatus,
    role
  )

  if (CL_1324_NEW_NO_PERMISSION_PAGE) {
    if (isTrialMode && isTrialExpired && !isAdmin && !hasNoPermission) {
      window.location.replace('/#/')
    }
  } else {
    if (isTrialMode && isTrialExpired && !isAdmin) {
      window.location.replace('/#/')
    }
  }

  return (
    <AppStyled>
      <ConnectedAppHeader />

      <AppContentWrapper>
        {IS_DEV && <ProcurifyDevToolsLoadable />}

        {!CL_1324_NEW_NO_PERMISSION_PAGE && <ConnectedNavigation />}

        {CL_1324_NEW_NO_PERMISSION_PAGE && !hasNoPermission && (
          <ConnectedNavigation />
        )}

        <ErrorBoundary>
          <SystemPushNotifications />
          <GlobalAnalytics
            user={user}
            segmentAnalyticsMetadata={segmentAnalyticsMetadata}
          />
          <PaginationProvider>
            <ModalProvider>
              <SheetProvider>
                <ModalRoot />
                <AppContent>
                  {IS_DEV && <ProcurifyDevToolsFfWarning />}

                  <Switch>
                    <Route
                      exact
                      path='/'
                      render={() => {
                        window.location.reload()
                        return null
                      }}
                    />
                    <Route
                      path='/spending-card'
                      component={SpendingCardRouter}
                    />
                    {enablePurchasingRoutes && (
                      <ProtectedRoute
                        path='/catalog'
                        component={CatalogPage}
                        navigationPermissions={['procure__product_catalog']}
                      />
                    )}
                    <Route path='/profile' component={UserProfileRouter} />
                    <Route path='/welcome' component={WelcomePage} />
                    <Route path='/settings' component={SettingsRouter} />
                    <Route path='/expenses' component={ExpenseRouter} />
                    {/* Accounts Payable pages */}
                    {AP_10029_UNBILLED_ITEMS_PAGE_MIGRATION && (
                      <Route
                        path='/unbilled'
                        component={UnbilledItemPageRouter}
                      />
                    )}
                    {/* Bill pages */}
                    <Route path='/bills' component={BillRouter} />
                    {/* Contract pages */}
                    <Route path='/contracts' component={ContractRouter} />
                    {/* Financial account page */}
                    {enableEverythingButExpenseApRoutes && (
                      <ProtectedRoute
                        path={brp('FinancialAccountPage')}
                        component={PaymentFinancialAccountPage}
                        customFilter={({ featureSwitches, permissions }) => {
                          return (
                            (featureSwitches.bill_pay ||
                              featureSwitches.enable_airwallex_issuing ||
                              featureSwitches.enable_airwallex_onboarding) &&
                            permissions.includes(
                              Permissions.SETTINGS_MANAGE_FINANCIAL_ACCOUNT
                            )
                          )
                        }}
                      />
                    )}
                    {/* Payment pages */}
                    <Route path='/payments' component={PaymentRouter} />
                    {/* Procurement */}
                    <Route path='/procurement' component={ProcurementRouter} />
                    {/* Data and Insights */}
                    <Route
                      path='/analytics'
                      component={DataAndInsightsRouter}
                    />
                    {/* Order pages */}
                    <Route path='/orders' component={OrderRouter} />
                    {/* PO pages */}
                    <Route
                      path='/purchase-orders'
                      component={PurchaseOrderRouter}
                    />
                    {/* Receive pages */}
                    <Route path='/receive' component={ReceiveRouter} />
                    {/* Integration pages */}
                    <Route
                      path='/integrations'
                      component={IntegrationsRouter}
                    />
                    {/* Instance management pages */}
                    <Route
                      path='/instance-management'
                      component={InstanceManagementRouter}
                    />
                    {/* Vendors pages */}
                    <Route path='/vendors' component={VendorRouter} />
                    <Route path='/vendor' component={VendorRouter} />

                    <Route path='/redirect' component={RedirectPage} />
                    {IS_DEV && (
                      <Route path='/dev' component={DevelopmentPage} />
                    )}
                    {CL_1324_NEW_NO_PERMISSION_PAGE && hasNoPermission && (
                      <Route
                        path='/permissions-pending'
                        component={NoPermissionsPage}
                      />
                    )}
                    <Route path='*' render={() => <Redirect to='/' />} />
                  </Switch>
                </AppContent>
              </SheetProvider>
            </ModalProvider>
          </PaginationProvider>
          {IS_PROD && <Intercom />}
        </ErrorBoundary>
        <ToastContainerWrapper />
      </AppContentWrapper>
    </AppStyled>
  )
}

const PublicRoutes = () => {
  return [<Route key='login' path='/login' component={LoginPage} />]
}

export const App = () => {
  const history = useHistory()

  const {
    isLoading,
    isAuthenticated,
    error: authError,
    errorCode: authErrorCode,
  } = useAuthentication()
  const isAppReady = useSelector(makeSelectIsAppReady())

  const showAuthenticatedApp = isAuthenticated && isAppReady

  React.useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      history.push('/login')
    }
  }, [isLoading, isAuthenticated, history])

  React.useEffect(() => {
    if (authErrorCode === AuthenticationErrorTypes.STAFF_ACCESS_EXPIRED) {
      window.location.replace(logoutUrl)
    }
  }, [authErrorCode])

  const renderApplication = () => {
    if (isLoading) return <AppLoader />

    if (authError) return <GlobalErrorScreen />

    return (
      <Switch>
        {PublicRoutes()}

        {showAuthenticatedApp && (
          <Route path='*' component={AuthenticatedApp} />
        )}
      </Switch>
    )
  }

  return (
    <ThemeWrapper theme={theme}>
      <UIProvider>
        <GlobalStyles />

        {renderApplication()}
      </UIProvider>
    </ThemeWrapper>
  )
}
